import { z } from "zod";
import {
  LanguageEnumSchema,
  OrgStatusEnumValue,
  UserRoleEnumValue,
  ZodErrorMessageEnumValue,
} from "./enums.js";

export enum OrgListOfNonWorkingDaysEnum {
  MON = "MON",
  TUE = "TUE",
  WED = "WED",
  THU = "THU",
  FRI = "FRI",
  SAT = "SAT",
  SUN = "SUN",
}
export enum OrgListOfRiskTypeEnum {
  TECHNIQUE = "Technique",
  HUMAIN = "Humain",
  JURIDIQUE = "Juridique",
  FINANCIER = "Financier",

}
const disposableEmailDomains = [
  "mailinator.com",
  "guerrillamail.com",
  "10minutemail.com",
  "tempmail.com",
  // "yopmail.com",
];
export const organisationIdSchema = z.string().uuid();

export const createOrganisationSchema = z.object({
  organisationName: z.string().min(1),
  industry: z.string().min(1),
  status: z.nativeEnum(OrgStatusEnumValue),
  country: z.string().min(1),
  phoneNumber: z.string({required_error:"Please insert a valid phone number in organisation details"}).max(20,"Phone number is limited to 20 digits" ),
  countryCode: z.string().min(1).optional(),
  nonWorkingDays: z.nativeEnum(OrgListOfNonWorkingDaysEnum).array().optional(),
  createDemoProjects: z.boolean().optional(),
});

const severityTypeSchema = z.array(
  z.object({
    severityId: z.string().uuid().optional(),
    min: z.number().int(),
    max: z.number().int(),
    type: z.string().min(1),
  })
);

export const updateOrganisationSchema = z.object({
  organisationName: z.string({ required_error: ZodErrorMessageEnumValue.REQUIRED }).min(1),
  industry: z.string().min(1).optional(),
  status: z.nativeEnum(OrgStatusEnumValue).optional(),
  country: z.string().min(1).optional(),
  nonWorkingDays: z.nativeEnum(OrgListOfNonWorkingDaysEnum).array().optional(),
  jobTitlesOfOrg: z.string().array().optional(),
  riskType: z.string().array().optional(),
  phoneNumber: z.string({required_error:"Please insert a valid phone number in organisation details"}).max(20,"Phone number is limited to 20 digits" ),
  countryCode: z.string().min(1).optional(),
  userId: z.string().uuid(),
  severityTypes: severityTypeSchema.optional(),
});

export const addOrganisationMemberSchema = z.object({
  email: z
    .string({ required_error: ZodErrorMessageEnumValue.REQUIRED })
    .email({ message: "Email is not valid" })
    .refine(
      (email) => {
        const domain = email.split("@")[1];
        return !disposableEmailDomains.includes(domain ?? "");
      },
      {
        message: "Please use a permanent email address to sign up",
      }
    ),
  language: z.nativeEnum(LanguageEnumSchema).optional(),
  role: z.nativeEnum(UserRoleEnumValue).refine(
    (value) => {
      return (
        value in UserRoleEnumValue &&
        (value === UserRoleEnumValue.TEAM_MEMBER ||
          value === UserRoleEnumValue.PROJECT_MANAGER)
      );
    },
    {
      message: "Only team member and project manager role allowed",
    }
  ),
  isAdmin: z.boolean(),
  projectId: z.string().uuid().optional(),
});

export const organisationStatuSchema = z.object({
  status: z.nativeEnum(OrgStatusEnumValue),
});

export const memberRoleSchema = z.object({
  role: z.nativeEnum(UserRoleEnumValue).refine(
    (value) => {
      return (
        value in UserRoleEnumValue &&
        (value === UserRoleEnumValue.PROJECT_MANAGER ||
          value === UserRoleEnumValue.TEAM_MEMBER)
      );
    },
    {
      message: "Only team member and project manager roles are allowed",
    }
  ),
});

export const reAssginedTaskSchema = z.object({
  oldUserId: z.string().uuid().array(),
  newUserId: z.string().uuid(),
});

export const assignProjectAndRoleToUserSchema = z.object({
  isRoleChangePmToTm: z.boolean(),
  arrayOfRoleAndProjectId: z
    .object({
      projectRoleForUser: z.nativeEnum(UserRoleEnumValue),
      projectId: z.string().uuid(),
    })
    .array(),
  isCollaborator: z.boolean().default(false),
});

export const addMemberToOrgSchema = z.object({
  email: z
    .string({ required_error: ZodErrorMessageEnumValue.REQUIRED })
    .email({ message: "Email is not valid" })
    .refine(
      (email) => {
        const domain = email.split("@")[1];
        return !disposableEmailDomains.includes(domain ?? "");
      },
      {
        message: "Please use a permanent email address to sign up",
      }
    ),
  language: z.nativeEnum(LanguageEnumSchema).optional(),
  isAdmin: z.boolean(),
  isCollaborator: z.boolean().default(false),
});

export const organisationUserBlockUnblockSchema = z.object({
  organisationId: z.string().uuid(),
  userOrganisationId: z.string().uuid(),
});

export const roleChangePmToTmSchema = z.object({
  projectId: z.string().uuid(),
  newProjectManagerUserId: z.string().uuid(),
});
